import { useState, useCallback, useEffect } from "react";
import { useLocation } from "react-router-dom";
import html2canvas from "html2canvas";
import api from "../../lib/api";
import bugContext from "../../lib/bugContext";
import { toast } from "sonner";
import {
  Bug, X, Send, AlertTriangle, AlertCircle, Info, Sparkles,
  Camera, Loader2, Monitor, MousePointer, Terminal, Wifi, Layout,
  ChevronDown, ChevronUp,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Badge } from "../ui/badge";

const SEVERITY_OPTIONS = [
  { value: "critical", label: "Critical", icon: AlertTriangle, color: "text-red-600" },
  { value: "major", label: "Major", icon: AlertCircle, color: "text-orange-500" },
  { value: "minor", label: "Minor", icon: Info, color: "text-yellow-500" },
  { value: "cosmetic", label: "Cosmetic", icon: Sparkles, color: "text-blue-400" },
];

function detectModule(pathname) {
  const map = {
    "/sales": "Sales", "/quotations": "Sales", "/pricelists": "Sales",
    "/purchase": "Purchase", "/vendors": "Purchase",
    "/production": "Production", "/shop-floor": "Production", "/planning": "Production",
    "/inventory": "Inventory", "/dispatch-notes": "Inventory",
    "/quality": "Quality", "/finance": "Finance", "/expenses": "Finance",
    "/hr": "HR", "/leaves": "HR",
    "/security": "Security", "/maintenance": "Maintenance",
    "/settings": "Settings", "/masters": "Masters",
    "/marketing": "Marketing", "/reports": "Reports",
    "/approvals": "Approvals", "/tasks": "Tasks",
    "/crm": "CRM", "/notes": "Notes", "/meetings": "Meetings",
    "/": "Dashboard",
  };
  for (const [prefix, mod] of Object.entries(map).sort((a, b) => b[0].length - a[0].length)) {
    if (pathname === prefix || pathname.startsWith(prefix + "/")) return mod;
  }
  return "Other";
}

function ContextItem({ icon: Icon, label, value, color = "text-muted-foreground" }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2 text-xs">
      <Icon className={`w-3 h-3 mt-0.5 shrink-0 ${color}`} />
      <span className="text-muted-foreground">{label}:</span>
      <span className="font-medium text-foreground break-all">{value}</span>
    </div>
  );
}

export default function BugReporter() {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [showContext, setShowContext] = useState(false);
  const [context, setContext] = useState(null);
  const location = useLocation();
  const detectedModule = detectModule(location.pathname);

  const [form, setForm] = useState({
    title: "", description: "", severity: "", steps_to_reproduce: "", screenshot_url: "",
  });
  const [screenshotPreview, setScreenshotPreview] = useState(null);

  const resetForm = () => {
    setForm({ title: "", description: "", severity: "", steps_to_reproduce: "", screenshot_url: "" });
    setScreenshotPreview(null);
    setDuplicates([]);
    setContext(null);
    setShowContext(false);
  };

  // On dialog open: capture context snapshot + auto-fill from errors
  useEffect(() => {
    if (!open) return;
    const snap = bugContext.getSnapshot();
    setContext(snap);

    // Auto-fill title and description from recent errors/API failures
    const suggestedTitle = bugContext.suggestTitle();
    const suggestedDesc = bugContext.suggestDescription();
    if (suggestedTitle || suggestedDesc) {
      setForm(f => ({
        ...f,
        title: f.title || suggestedTitle,
        description: f.description || suggestedDesc,
      }));
    }
  }, [open]);

  const captureScreenshot = async () => {
    setCapturing(true);
    try {
      const dialogs = document.querySelectorAll('[role="dialog"]');
      const overlay = document.querySelector('[data-radix-portal]');
      if (overlay) overlay.style.display = "none";
      dialogs.forEach(d => d.style.display = "none");

      await new Promise(r => setTimeout(r, 100));
      const canvas = await html2canvas(document.body, {
        scale: 0.5, useCORS: true, logging: false, windowWidth: window.innerWidth, windowHeight: window.innerHeight,
      });

      if (overlay) overlay.style.display = "";
      dialogs.forEach(d => d.style.display = "");

      const blob = await new Promise(resolve => canvas.toBlob(resolve, "image/png", 0.7));
      const formData = new FormData();
      formData.append("file", blob, "screenshot.png");

      const res = await api.post("/bug-reports/upload-screenshot", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setForm(f => ({ ...f, screenshot_url: res.data.url }));
      setScreenshotPreview(canvas.toDataURL("image/png", 0.5));
      toast.success("Screenshot captured!");
    } catch (e) {
      console.error("Screenshot failed:", e);
      toast.error("Failed to capture screenshot");
    } finally {
      setCapturing(false);
    }
  };

  const checkDuplicates = useCallback(async (title, description) => {
    if (title.length < 5 && description.length < 10) { setDuplicates([]); return; }
    try {
      const res = await api.post("/bug-reports/check-duplicates", {
        title, description, module: detectModule(location.pathname).toLowerCase(),
      });
      setDuplicates(res.data.duplicates || []);
    } catch { /* ignore */ }
  }, [location.pathname]);

  const handleTitleBlur = () => checkDuplicates(form.title, form.description);
  const handleDescBlur = () => checkDuplicates(form.title, form.description);

  const handleSubmit = async () => {
    if (!form.title.trim() || !form.description.trim()) {
      toast.error("Please fill in title and description");
      return;
    }
    setSubmitting(true);
    try {
      // Build enriched context payload
      const contextPayload = {};
      if (context) {
        contextPayload.screen_info = context.screen;
        contextPayload.active_tab = context.activeTab || "";
        if (context.lastClickedElement) {
          contextPayload.last_clicked = `${context.lastClickedElement.tag}: "${context.lastClickedElement.text}" (${context.lastClickedElement.selector})`;
        }
        if (context.consoleErrors.length > 0) {
          contextPayload.console_errors = context.consoleErrors.map(
            e => `[${e.type}] ${e.message}`
          );
        }
        if (context.apiFailures.length > 0) {
          contextPayload.api_failures = context.apiFailures.map(
            f => `${f.method} ${f.url} → ${f.status} ${f.statusText}: ${f.detail}`
          );
        }
      }

      await api.post("/bug-reports", {
        ...form,
        page_url: window.location.pathname,
        browser_info: navigator.userAgent,
        auto_context: contextPayload,
      });
      toast.success("Bug reported successfully! Thank you for helping improve the system.");
      bugContext.clear();
      resetForm();
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit report");
    } finally {
      setSubmitting(false);
    }
  };

  const hasErrors = context && (context.consoleErrors.length > 0 || context.apiFailures.length > 0);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 z-50 flex items-center gap-2 px-4 py-2.5 rounded-full bg-red-600 hover:bg-red-700 text-white shadow-lg hover:shadow-xl transition-all duration-200"
        data-testid="bug-report-fab"
      >
        <Bug className="w-4 h-4" />
        <span className="text-sm font-medium hidden sm:inline">Report Bug</span>
        {hasErrors && (
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full animate-pulse" />
        )}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="bug-report-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bug className="w-5 h-5 text-red-500" /> Report a Bug
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Auto-Captured Context Banner */}
            <div className="rounded-md border bg-muted/30 p-3 space-y-2" data-testid="auto-context-banner">
              <div
                className="flex items-center justify-between cursor-pointer"
                onClick={() => setShowContext(!showContext)}
              >
                <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground">
                  <Info className="w-3.5 h-3.5" />
                  Auto-Captured Info
                  {hasErrors && (
                    <Badge variant="destructive" className="text-[10px] h-4 px-1.5">
                      {(context?.consoleErrors.length || 0) + (context?.apiFailures.length || 0)} errors detected
                    </Badge>
                  )}
                </div>
                {showContext ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />}
              </div>

              {/* Always visible summary row */}
              <div className="flex flex-wrap items-center gap-2 text-[11px]">
                <Badge variant="outline" className="text-[10px] gap-1">
                  <Layout className="w-2.5 h-2.5" /> {location.pathname}
                </Badge>
                <Badge variant="outline" className="text-[10px]">{detectedModule}</Badge>
                {context?.activeTab && (
                  <Badge variant="outline" className="text-[10px] gap-1 bg-blue-50 dark:bg-blue-950/20">
                    Tab: {context.activeTab}
                  </Badge>
                )}
                {context?.screen && (
                  <Badge variant="outline" className="text-[10px] gap-1">
                    <Monitor className="w-2.5 h-2.5" /> {context.screen.viewport}
                    {context.screen.isMobile && " (Mobile)"}
                  </Badge>
                )}
              </div>

              {/* Expanded context details */}
              {showContext && context && (
                <div className="space-y-2 pt-1 border-t mt-2" data-testid="expanded-context">
                  <ContextItem icon={Layout} label="Page" value={location.pathname} />
                  <ContextItem icon={Layout} label="Module" value={detectedModule} />
                  {context.activeTab && (
                    <ContextItem icon={Layout} label="Active Tab" value={context.activeTab} color="text-blue-500" />
                  )}
                  <ContextItem icon={Monitor} label="Viewport" value={`${context.screen.viewport} @ ${context.screen.devicePixelRatio}x`} />
                  <ContextItem icon={Monitor} label="Screen" value={context.screen.screenResolution} />

                  {context.lastClickedElement && (
                    <ContextItem
                      icon={MousePointer}
                      label="Last Clicked"
                      value={`<${context.lastClickedElement.tag}> "${context.lastClickedElement.text}" — ${context.lastClickedElement.selector}`}
                      color="text-purple-500"
                    />
                  )}

                  {context.consoleErrors.length > 0 && (
                    <div className="space-y-1">
                      <div className="flex items-center gap-1.5 text-xs font-medium text-red-600">
                        <Terminal className="w-3 h-3" /> Console Errors ({context.consoleErrors.length})
                      </div>
                      {context.consoleErrors.slice(-3).map((e, i) => (
                        <div key={i} className="text-[11px] bg-red-50 dark:bg-red-950/20 rounded px-2 py-1 font-mono text-red-700 dark:text-red-400 break-all">
                          [{e.type}] {e.message}
                        </div>
                      ))}
                    </div>
                  )}

                  {context.apiFailures.length > 0 && (
                    <div className="space-y-1">
                      <div className="flex items-center gap-1.5 text-xs font-medium text-orange-600">
                        <Wifi className="w-3 h-3" /> Failed API Calls ({context.apiFailures.length})
                      </div>
                      {context.apiFailures.slice(-3).map((f, i) => (
                        <div key={i} className="text-[11px] bg-orange-50 dark:bg-orange-950/20 rounded px-2 py-1 font-mono text-orange-700 dark:text-orange-400 break-all">
                          {f.method} {f.url} → {f.status}: {f.detail}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div>
              <Label>Title *</Label>
              <Input
                value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                onBlur={handleTitleBlur}
                placeholder="Brief summary of the issue" className="rounded-md" data-testid="bug-title-input"
              />
              {form.title && hasErrors && (
                <p className="text-[10px] text-green-600 mt-0.5">Auto-filled from detected error</p>
              )}
            </div>

            <div>
              <Label>Description *</Label>
              <Textarea
                value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                onBlur={handleDescBlur}
                placeholder="What happened? What did you expect?" rows={3} className="rounded-md resize-none" data-testid="bug-description-input"
              />
            </div>

            {/* Duplicate Warning */}
            {duplicates.length > 0 && (
              <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800 p-3 space-y-2" data-testid="duplicate-warning">
                <div className="flex items-center gap-2 text-xs font-semibold text-amber-700 dark:text-amber-400">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Possible duplicates found ({duplicates.length})
                </div>
                {duplicates.map(d => (
                  <div key={d.id} className="flex items-center justify-between text-xs bg-white dark:bg-background rounded px-2 py-1.5 border">
                    <span className="font-medium truncate max-w-[250px]">{d.title}</span>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge variant="outline" className="text-[10px]">{d.similarity}% match</Badge>
                      <Badge variant="outline" className="text-[10px]">{d.status}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div>
              <Label>Steps to Reproduce</Label>
              <Textarea
                value={form.steps_to_reproduce} onChange={e => setForm(f => ({ ...f, steps_to_reproduce: e.target.value }))}
                placeholder={"1. Go to...\n2. Click on...\n3. See error..."} rows={3} className="rounded-md resize-none text-sm" data-testid="bug-steps-input"
              />
            </div>

            <div>
              <Label>Severity</Label>
              <Select value={form.severity} onValueChange={v => setForm(f => ({ ...f, severity: v }))}>
                <SelectTrigger className="rounded-md" data-testid="bug-severity-select"><SelectValue placeholder="Auto-detect (or choose)" /></SelectTrigger>
                <SelectContent>
                  {SEVERITY_OPTIONS.map(s => (
                    <SelectItem key={s.value} value={s.value}>
                      <span className="flex items-center gap-2"><s.icon className={`w-3.5 h-3.5 ${s.color}`} />{s.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground mt-1">Leave empty to let AI auto-detect severity</p>
            </div>

            {/* Screenshot */}
            <div>
              <Label>Screenshot</Label>
              <div className="flex items-center gap-2 mt-1">
                <Button
                  type="button" variant="outline" size="sm"
                  onClick={captureScreenshot} disabled={capturing}
                  className="gap-2 rounded-md" data-testid="capture-screenshot-btn"
                >
                  {capturing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
                  {capturing ? "Capturing..." : "Capture Page Screenshot"}
                </Button>
                {screenshotPreview && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => { setScreenshotPreview(null); setForm(f => ({ ...f, screenshot_url: "" })); }} className="text-xs text-destructive">
                    Remove
                  </Button>
                )}
              </div>
              {screenshotPreview && (
                <div className="mt-2 rounded-md border overflow-hidden" data-testid="screenshot-preview">
                  <img src={screenshotPreview} alt="Screenshot" className="w-full max-h-32 object-cover object-top" />
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { resetForm(); setOpen(false); }} className="rounded-md">
              <X className="w-3.5 h-3.5 mr-1.5" /> Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={submitting} className="rounded-md bg-red-600 hover:bg-red-700" data-testid="bug-submit-btn">
              <Send className="w-3.5 h-3.5 mr-1.5" />
              {submitting ? "Submitting..." : "Submit Report"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
